import type { CoachBriefV2, Fact } from "@arc/brief-schema";
import type {
  CoachAnnotation,
  CoachAssignment,
  CoachDrillTemplate,
  CoachDrillTemplateDraft,
  CoachDrillTemplateFocusTag,
  CoachRangePlanKind,
  CoachRangePlanPortalStatus,
  CoachRangePlanPrescription,
} from "./coach";

const ACTIVE_ASSIGNMENT_STATUSES = new Set(["draft", "sent", "acknowledged"]);
const STALE_BRIEF_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
const VERDICT_OVERRIDE_PATTERN = /\b(verdict|read|review|restatement)\b/i;

export interface ReusableCoachPattern {
  assignmentId: string;
  updatedAt: string;
  plan: CoachRangePlanPrescription;
}

export type BuilderTemplateFilterId = "all" | CoachDrillTemplateFocusTag;

export type PracticeWindowId =
  | "single_session"
  | "weekly_bridge"
  | "monthly_bridge";

const PRACTICE_WINDOW_TITLE_SUFFIX =
  /\s+·\s+(Single session|Weekly bridge|Monthly bridge(?:\s+\(4 sessions\))?)$/;
const PRACTICE_WINDOW_MARKER = / ?Practice window:[^.]+\.(?: [^.]+\.)*/g;
const EMBEDDED_RANGE_PLAN_PATTERN = /<!--\s*arc-range-plan:([\s\S]+?)\s*-->/;
const BUILDER_TEMPLATE_FILTER_ORDER: CoachDrillTemplateFocusTag[] = [
  "general",
  "start_line",
  "tempo",
  "posture",
  "contact",
  "short_game",
  "putting",
  "mobility",
  "mental",
];

type EmbeddedRangePlanPayload = {
  version: 1;
  plan: CoachRangePlanPrescription;
};

export type MediaReviewSeedItem = {
  sessionId: string;
  sessionLabel: string;
  swingId: string;
  title: string;
  summary: string;
  club: string;
};

export type MediaReviewSeed = {
  sessionId: string;
  sessionLabel: string;
  swingId: string | null;
  title: string;
  summary: string;
  club: string;
};

export type LatestRangePlanStatusSummary = {
  progressLabel: string;
  headline: string;
  detail: string;
  tone: "active" | "muted" | "positive";
};

function titleOrLabelText(plan: CoachRangePlanPrescription): string {
  return `${plan.title} ${plan.focusLabel} ${plan.clubLabel}`.toLowerCase();
}

export function focusTagForRangePlan(
  plan: CoachRangePlanPrescription,
): CoachDrillTemplateFocusTag {
  const text = titleOrLabelText(plan);
  if (text.includes("putt")) return "putting";
  if (text.includes("chip") || text.includes("pitch") || text.includes("bunker")) {
    return "short_game";
  }
  if (
    text.includes("start-line") ||
    text.includes("start line") ||
    text.includes("face") ||
    text.includes("gate")
  ) {
    return "start_line";
  }
  if (text.includes("tempo")) return "tempo";
  if (text.includes("posture") || text.includes("setup")) return "posture";
  if (
    text.includes("contact") ||
    text.includes("window") ||
    text.includes("wedge") ||
    text.includes("carry")
  ) {
    return "contact";
  }
  if (text.includes("mobility")) return "mobility";
  if (text.includes("mental") || text.includes("routine")) return "mental";
  return "general";
}

export function builderTemplateFilters(
  focusTags: CoachDrillTemplateFocusTag[],
): BuilderTemplateFilterId[] {
  const seen = new Set(focusTags);
  return ["all", ...BUILDER_TEMPLATE_FILTER_ORDER.filter((tag) => seen.has(tag))];
}

function stripEmbeddedRangePlan(detail: string): string {
  return detail.replace(EMBEDDED_RANGE_PLAN_PATTERN, "").trim();
}

function encodeEmbeddedRangePlan(
  plan: CoachRangePlanPrescription,
): string {
  const payload: EmbeddedRangePlanPayload = {
    version: 1,
    plan,
  };
  return encodeURIComponent(JSON.stringify(payload));
}

function decodeEmbeddedRangePlan(detail: string): CoachRangePlanPrescription | null {
  const match = detail.match(EMBEDDED_RANGE_PLAN_PATTERN);
  if (!match?.[1]) return null;

  try {
    const decoded = JSON.parse(decodeURIComponent(match[1])) as EmbeddedRangePlanPayload;
    if (decoded.version !== 1 || !decoded.plan) return null;
    return decoded.plan;
  } catch {
    return null;
  }
}

export function createRangePlanTemplateDraft(
  plan: CoachRangePlanPrescription,
  detail: string,
): CoachDrillTemplateDraft {
  const cleanDetail = stripEmbeddedRangePlan(detail);
  return {
    title: plan.title,
    detail: `${cleanDetail}\n\n<!-- arc-range-plan:${encodeEmbeddedRangePlan(plan)} -->`.trim(),
    focusTag: focusTagForRangePlan(plan),
    defaultDurationSwings: plan.totalBalls,
    defaultDurationMinutes: null,
  };
}

export function rangePlanPrescriptionFromTemplate(
  template: Pick<CoachDrillTemplate, "id" | "title" | "detail">,
  presets: CoachRangePlanPrescription[],
): CoachRangePlanPrescription | null {
  const embedded = decodeEmbeddedRangePlan(template.detail);
  if (embedded) {
    return {
      ...embedded,
      templateId: template.id,
    };
  }

  return (
    presets.find((preset) => preset.templateId === template.id) ??
    presets.find((preset) => template.title.includes(preset.title)) ??
    null
  );
}

export function buildStudentMediaPath(
  collaborationId: string,
  options: { sessionId?: string | null; swingId?: string | null } = {},
): string {
  const params = new URLSearchParams();
  if (options.sessionId) params.set("session", options.sessionId);
  if (options.swingId) params.set("swing", options.swingId);
  const query = params.toString();
  return `/students/${encodeURIComponent(collaborationId)}/media${query ? `?${query}` : ""}`;
}

export function buildReviewedSessionCoachPath(
  collaborationId: string,
  options: { sessionId: string; swingId?: string | null },
): string {
  const params = new URLSearchParams();
  params.set("reviewSession", options.sessionId);
  if (options.swingId) params.set("reviewSwing", options.swingId);
  return `/students/${encodeURIComponent(collaborationId)}?${params.toString()}#assign-next-session`;
}

export function mediaReviewSeedFromItems(
  items: MediaReviewSeedItem[],
  options: { sessionId?: string | null; swingId?: string | null },
): MediaReviewSeed | null {
  if (items.length === 0) return null;

  const selectedSwing = options.swingId
    ? items.find((item) => item.swingId === options.swingId)
    : null;
  if (selectedSwing) {
    return {
      sessionId: selectedSwing.sessionId,
      sessionLabel: selectedSwing.sessionLabel,
      swingId: selectedSwing.swingId,
      title: selectedSwing.title,
      summary: selectedSwing.summary,
      club: selectedSwing.club,
    };
  }

  const selectedSession = options.sessionId
    ? items.find((item) => item.sessionId === options.sessionId)
    : null;
  if (selectedSession) {
    return {
      sessionId: selectedSession.sessionId,
      sessionLabel: selectedSession.sessionLabel,
      swingId: selectedSession.swingId,
      title: selectedSession.title,
      summary: selectedSession.summary,
      club: selectedSession.club,
    };
  }

  return null;
}

export function latestRangePlanStatusSummary(
  status: CoachRangePlanPortalStatus,
): LatestRangePlanStatusSummary {
  const progressLabel = `${status.completedBalls} of ${status.totalBalls} balls`;
  const currentBlock = status.blocks.find((block) => block.state === "active") ?? null;
  const completedBlock =
    currentBlock ??
    status.blocks
      .filter((block) => block.state === "completed")
      .slice(-1)[0] ??
    null;

  switch (status.state) {
    case "completed":
      return {
        progressLabel,
        headline: "Plan completed",
        detail: completedBlock
          ? `${completedBlock.title} finished · ${completedBlock.completedBalls} of ${completedBlock.totalBalls} balls`
          : "All assigned balls were completed.",
        tone: "positive",
      };
    case "paused":
      return {
        progressLabel,
        headline: status.currentBlockTitle
          ? `Paused in ${status.currentBlockTitle}`
          : "Plan paused",
        detail: currentBlock
          ? `${currentBlock.completedBalls} of ${currentBlock.totalBalls} balls · ${currentBlock.club} · ${currentBlock.camera}`
          : status.nextBlockTitle
            ? `Next block: ${status.nextBlockTitle}`
            : "The player stopped before the plan was complete.",
        tone: "muted",
      };
    case "active":
    default:
      return {
        progressLabel,
        headline: status.currentBlockTitle
          ? `In ${status.currentBlockTitle}`
          : "Plan in progress",
        detail: currentBlock
          ? `${currentBlock.completedBalls} of ${currentBlock.totalBalls} balls · ${currentBlock.club} · ${currentBlock.camera}`
          : status.nextBlockTitle
            ? `Next block: ${status.nextBlockTitle}`
            : "The player is working through the assigned plan.",
        tone: "active",
      };
  }
}

function assignmentTimestamp(assignment: CoachAssignment): number {
  return Date.parse(assignment.updatedAt || assignment.createdAt || "");
}

function annotationTimestamp(annotation: CoachAnnotation): number {
  return Date.parse(annotation.updatedAt || annotation.createdAt || "");
}

export function latestActiveHumanPlan(
  assignments: CoachAssignment[],
): CoachAssignment | null {
  return [...assignments]
    .filter(
      (assignment) =>
        !assignment.isCue &&
        assignment.authorKind === "coach" &&
        ACTIVE_ASSIGNMENT_STATUSES.has(assignment.status),
    )
    .sort((left, right) => assignmentTimestamp(right) - assignmentTimestamp(left))[0] ?? null;
}

export function latestArcAssistPlan(
  assignments: CoachAssignment[],
): CoachAssignment | null {
  return [...assignments]
    .filter(
      (assignment) =>
        !assignment.isCue &&
        assignment.authorKind === "arc_ai" &&
        ACTIVE_ASSIGNMENT_STATUSES.has(assignment.status),
    )
    .sort((left, right) => assignmentTimestamp(right) - assignmentTimestamp(left))[0] ?? null;
}

export function verdictTopFacts(brief: CoachBriefV2 | null): Fact[] {
  if (!brief) return [];
  if (brief.executiveSummary.topFacts.length > 0) {
    return brief.executiveSummary.topFacts.slice(0, 3);
  }

  const clusterFacts = brief.missPattern.clusters.slice(0, 2).map((cluster) => ({
    label: "Miss cluster",
    value: cluster,
    delta: null,
    provenance: "estimated" as const,
    source: "Pattern history",
  }));
  if (clusterFacts.length > 0) return clusterFacts;

  if (brief.missPattern.costlyMiss) {
    return [
      {
        label: "Costly miss",
        value: brief.missPattern.costlyMiss,
        delta: null,
        provenance: "estimated",
        source: "Packet summary",
      },
    ];
  }

  return [];
}

export function isBriefStale(
  brief: CoachBriefV2 | null,
  nowMs = Date.now(),
): boolean {
  if (!brief) return false;
  const generatedAtMs = Date.parse(brief.meta.generatedAt);
  if (!Number.isFinite(generatedAtMs)) return false;
  return nowMs - generatedAtMs > STALE_BRIEF_WINDOW_MS;
}

export function latestCoachVerdictOverride(
  annotations: CoachAnnotation[],
): CoachAnnotation | null {
  return [...annotations]
    .filter((annotation) => {
      if (
        annotation.authorKind !== "coach" ||
        annotation.tag !== "note" ||
        annotation.scope !== "session"
      ) {
        return false;
      }
      const title = annotation.title.trim();
      const body = annotation.body.trim();
      if (!body) return false;
      return VERDICT_OVERRIDE_PATTERN.test(title) || VERDICT_OVERRIDE_PATTERN.test(body);
    })
    .sort((left, right) => annotationTimestamp(right) - annotationTimestamp(left))[0] ?? null;
}

function reusablePatternKey(plan: CoachRangePlanPrescription): string {
  return [
    plan.templateId,
    plan.title,
    plan.kind,
    plan.focusLabel,
    plan.clubLabel,
    plan.totalBalls,
    ...plan.blocks.map((block) =>
      [block.title, block.club, block.camera, block.balls, block.goal].join("|")
    ),
  ].join("::");
}

export function reusableCoachPatterns(
  assignments: CoachAssignment[],
  limit = 4,
): ReusableCoachPattern[] {
  const seen = new Set<string>();
  return [...assignments]
    .filter(
      (assignment) =>
        !assignment.isCue &&
        assignment.authorKind === "coach" &&
        assignment.status !== "archived" &&
        assignment.rangePlanPrescription,
    )
    .sort((left, right) => assignmentTimestamp(right) - assignmentTimestamp(left))
    .flatMap((assignment) => {
      const plan = assignment.rangePlanPrescription;
      if (!plan) return [];
      const key = reusablePatternKey(plan);
      if (seen.has(key)) return [];
      seen.add(key);
      return [
        {
          assignmentId: assignment.id,
          updatedAt: assignment.updatedAt,
          plan,
        },
      ];
    })
    .slice(0, limit);
}

function stripPracticeWindowCopy(value: string): string {
  return value.replace(PRACTICE_WINDOW_MARKER, "").trim();
}

function stripPracticeWindowTitle(value: string): string {
  return value.replace(PRACTICE_WINDOW_TITLE_SUFFIX, "").trim();
}

function withAppendedSentence(base: string, sentence: string | null): string {
  const trimmedBase = stripPracticeWindowCopy(base);
  if (!sentence) return trimmedBase;
  return [trimmedBase, sentence].filter(Boolean).join(" ").trim();
}

export function applyPracticeWindowToPlan(
  plan: CoachRangePlanPrescription,
  practiceWindowId: PracticeWindowId,
): CoachRangePlanPrescription {
  const baseTitle = stripPracticeWindowTitle(plan.title);
  const baseSummary = stripPracticeWindowCopy(plan.summary);
  const baseSetup = stripPracticeWindowCopy(plan.setup);
  const baseBetweenBlocks = stripPracticeWindowCopy(plan.betweenBlocks);
  const baseHomework = stripPracticeWindowCopy(plan.homework);

  if (practiceWindowId === "single_session") {
    return {
      ...plan,
      title: baseTitle,
      summary: baseSummary,
      setup: baseSetup,
      betweenBlocks: baseBetweenBlocks,
      homework: baseHomework,
    };
  }

  if (practiceWindowId === "weekly_bridge") {
    return {
      ...plan,
      title: `${baseTitle} · Weekly bridge`,
      summary: withAppendedSentence(
        baseSummary,
        "Practice window: Weekly bridge before the next lesson with one full ARC-guided session.",
      ),
      setup: withAppendedSentence(
        baseSetup,
        "Practice window: Session 1 of 1 starts this week's lesson bridge. Use the full block order once before the next check-in.",
      ),
      betweenBlocks: withAppendedSentence(
        baseBetweenBlocks,
        "Practice window: Keep the same cue through every block so the next lesson starts from a fresh read, not a reset.",
      ),
      homework: withAppendedSentence(
        baseHomework,
        "Practice window: Complete one full session this week, save the required media, and send the final check-in within 24 hours.",
      ),
    };
  }

  return {
    ...plan,
    title: `${baseTitle} · Monthly bridge`,
    summary: withAppendedSentence(
      baseSummary,
      "Practice window: Monthly bridge across four practice sessions before the next lesson. Keep the same cue structure so each week connects cleanly.",
    ),
    setup: withAppendedSentence(
      baseSetup,
      "Practice window: Session 1 of 4 starts this week. Use the baseline block order first, then repeat the same plan once per week.",
    ),
    betweenBlocks: withAppendedSentence(
      baseBetweenBlocks,
      "Practice window: Week 2 repeats the plan, Week 3 keeps the same cue under transfer pressure, and Week 4 finishes with the verification pass.",
    ),
    homework: withAppendedSentence(
      baseHomework,
      "Practice window: Week 1 complete the plan once and log the baseline. Week 2 repeat the full plan with the same cue. Week 3 keep the same structure and note what still breaks under transfer. Week 4 finish the full plan, save the final media set, and send the month-end check-in before the next lesson.",
    ),
  };
}

export function createCustomRangePlanSeed({
  playerName,
  clubLabel,
  focusLabel,
  coachCue,
  kind = "coach_plan",
}: {
  playerName: string;
  clubLabel?: string | null;
  focusLabel?: string | null;
  coachCue?: string | null;
  kind?: CoachRangePlanKind;
}): CoachRangePlanPrescription {
  const resolvedClub = clubLabel?.trim() || "Assigned club";
  const resolvedFocus = focusLabel?.trim() || "Next-session work";
  const resolvedCue = coachCue?.trim() || "Hold one cue through the block order.";
  const blocks: CoachRangePlanPrescription["blocks"] = [
    {
      id: "warmup",
      balls: 10,
      title: "Warm-up",
      club: resolvedClub,
      camera: "Coach-selected angle",
      cadence: "Comfortable rhythm.",
      how: "Smooth swings at 70% effort. Don't correct anything yet.",
      goal: "Loosen up and find rhythm.",
      intent: "warmup",
    },
    {
      id: "baseline-read",
      balls: 18,
      title: "Baseline read",
      club: resolvedClub,
      camera: "Same angle",
      cadence: "7-8 seconds between balls.",
      how: "Stock swings only. No cue work yet.",
      goal: "Establish the starting pattern before changing anything.",
      intent: "stock",
      successCriteria: "Name the dominant miss before moving on.",
    },
    {
      id: "cue-block",
      balls: 24,
      title: "Cue block",
      club: resolvedClub,
      camera: "Same angle",
      cadence: "Same routine every ball.",
      how: "Apply the coach cue. Keep one target. Stop after two same-side misses and reset.",
      goal: "See whether the cue changes the miss pattern.",
      intent: "stock",
      successCriteria:
        "Miss pattern shifts toward neutral on the back half of the block.",
      checkpoint:
        "If the cue makes things worse, pause and message the coach before continuing.",
    },
    {
      id: "transfer-block",
      balls: 18,
      title: "Transfer block",
      club: `${resolvedClub} + one neighbor club`,
      camera: "Same angle",
      cadence: "Full routine.",
      how: "Alternate clubs every three balls while holding the same cue.",
      goal: "Check whether the cue survives a club change.",
      intent: "stock",
    },
    {
      id: "pressure-finish",
      balls: 12,
      title: "Pressure finish",
      club: resolvedClub,
      camera: "Same angle",
      cadence: "Full routine, no warm-up swings.",
      how: "Score each shot as playable or not. No cleanup reps after a miss.",
      goal: "Leave with an honest final read.",
      intent: "pressure",
      successCriteria:
        "At least 60% of the final 12 score as playable to the named target.",
    },
  ];

  return {
    id: "custom-plan",
    templateId: "custom-plan",
    title: `${playerName} next session`,
    kind,
    totalBalls: blocks.reduce((sum, block) => sum + block.balls, 0),
    summary: `A coach-built ${resolvedFocus.toLowerCase()} plan for the next range session.`,
    setup:
      "Start ARC in Coach Plan mode, choose the working club, and keep the first block on one target.",
    coachCue: resolvedCue,
    homework: "Complete the plan once before the next lesson and send the final check-in.",
    betweenBlocks:
      "Pause between blocks, name the dominant miss, and reset only when the next block is selected.",
    focusLabel: resolvedFocus,
    clubLabel: resolvedClub,
    blocks,
  };
}
